<script setup>
import { computed, inject } from 'vue';
import BiUnpivotAdvancedConfiguration from '~/bi/components/unpivot/bi-unpivot-advanced-configuration.vue';
import { useBiStore } from '~/bi/store/bi.store';
import { $toast } from '~/common/utils/common.utils';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  isPreview: {
    type: Boolean,
  },
});

const emit = defineEmits(['update:modelValue', 'back']);

const $t = inject('$t');
const bi_store = useBiStore();

const bi_advanced_config = ref(null);

function showToast(message) {
  $toast({ title: message, type: 'error' });
}

async function onSelection() {
  if (props.isPreview)
    return false;
  const config = await bi_advanced_config.value.getData();

  const title_alias = config.columns[0].header;
  const title_key = config.columns[0].data;
  const message = $t('Please fill the cells before proceeding');

  if (!title_alias) {
    showToast(message);
    return;
  }
  // filter out empty objects from config.data
  const dataRows = (config.data || []).filter(row => row && Object.keys(row).length > 0);

  // detect empty title cells
  const hasEmptyTitles = dataRows.some((row) => {
    const val = row[title_key];
    return !(val?.value ?? val);
  });

  // detect empty column cells (for any column beyond the first)
  let hasEmptyColumns = false;
  for (let i = 1; i < (config.columns || []).length; i++) {
    const colDataKey = config.columns[i].data;
    for (const row of dataRows) {
      const val = row[colDataKey];
      if (!(val?.value ?? val)) {
        hasEmptyColumns = true;
        break;
      }
    }
    if (hasEmptyColumns)
      break;
  }

  if (hasEmptyTitles || hasEmptyColumns) {
    showToast(message);
    return false;
  }

  const titles = dataRows.map(row => ({ field: row[title_key], alias: title_alias })).filter(d => d.field);
  const columns_array = (config.columns || []).map((col, col_index) => {
    if (col_index === 0)
      return undefined;
    const row_key = dataRows.map(row => row[col.data]?.value || row[col.data]).filter(d => d);
    const col_alias = config.columns[col_index].header;
    return row_key.map(key => ({
      field: key,
      alias: col_alias,
    }));
  }).filter(d => d);

  if (columns_array.length === 0) {
    showToast($t('Please add more columns'));
    return false;
  }

  const transpose = matrix => matrix[0].map((_, columnIndex) => matrix.map(row => row[columnIndex]));
  const columns_array_transpose = columns_array.length ? transpose(columns_array) : [];

  const unpivot_data = {
    type: 'union_all',
    rows: titles.map((title, index) => ({
      title,
      columns: columns_array_transpose[index],
    })),
  };
  emit('update:modelValue', { data: unpivot_data });
  return true;
}

const stages = bi_store.widget_builder_config.query.stages;
const all_columns = stages.flatMap(stage => stage.columns);
const columnOptions = all_columns.map(col => ({ value: col.alias, key: col.alias }));

defineExpose({
  onSelection,
});

const options = computed(() => {
  if (props.modelValue?.data?.rows?.length > 0) {
    const rows = props.modelValue.data.rows;
    const numCols = Math.max(...rows.map(r => (r.columns || []).length));
    const titleAlias = rows[0]?.title?.alias || 'Title';
    const titleKey = 'title';
    const columns = [{ header: titleAlias, data: titleKey, readOnly: false, type: 'text' }];
    for (let c = 0; c < numCols; c++) {
      const alias = rows[0]?.columns?.[c]?.alias || `Column ${c + 1}`;
      const dataKey = `col_${c}`;
      columns.push({ header: alias, data: dataKey, readOnly: false, type: 'autocomplete', filter: false, strict: true, trimDropdown: true, source: columnOptions });
    }
    const data = rows.map((r) => {
      const obj = {};
      obj[titleKey] = r.title?.field;
      for (let c = 0; c < numCols; c++) {
        obj[`col_${c}`] = r.columns?.[c]?.field;
      }
      return obj;
    });
    return { columns, data, type: 'autocomplete' };
  }
  else {
    return { columns: [], data: [] };
  }
});
</script>

<template>
  <div :class="isPreview ? '' : 'mt-6'">
    <div v-if="isPreview" class="text-sm font-semibold text-gray-900">
      <div class="flex items-center cursor-pointer mb-2" @click="$emit('back')">
        <IconHawkArrowLeft class="text-gray-600 max-w-4 h-4 size-4 mr-2" />
        {{ $t('Back to configuration') }}
      </div>
      <div>{{ $t('Preview results') }}</div>
    </div>
    <div v-else>
      <div class="text-sm font-semibold">
        {{ $t('Sample data') }}
      </div>
    </div>
    <div class=" mt-2 overflow-y-auto">
      <bi-unpivot-table :key="isPreview" class="mb-2" :height="350" :unpivot-data="isPreview ? modelValue?.data : null" />
      <div v-if="!isPreview">
        <BiUnpivotAdvancedConfiguration ref="bi_advanced_config" v-bind="options" :column-options="columnOptions" />
      </div>
    </div>
  </div>
</template>
